import React from 'react';
import {
  Box, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControlLabel, Table, TableBody, TableCell, TableHead, TableRow, Typography,
} from '@mui/material';
import InvButton from '../../../components/InvButton';
import { InvTextField } from '../../../components/InvField';
import { formatQty } from '../../../utils';
import { IPD_COLORS, cardSx } from '../../../theme';

const HEADERS = ['Medicine', 'Batch', 'Requested', 'Accept', 'Reject', 'Reason'];

const headCell = {
  color: IPD_COLORS.textSecondary,
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
};

/**
 * Presentational shell for the pharmacy accept/partial/reject decision.
 * Decision state, validation and submission stay with the tab.
 */
export default function WardReturnAcknowledgeDialog({
  open,
  loading,
  submitting,
  header,
  lines = [],
  decisions = {},
  reverseBilling,
  billable,
  resolveItemId,
  onClose,
  onSubmit,
  onAcceptedChange,
  onReasonChange,
  onAcceptAll,
  onRejectAll,
  onReverseBillingChange,
}) {
  const totals = lines.reduce((acc, line) => ({
    requested: acc.requested + Number(line.requested_quantity || 0),
    accepted: acc.accepted + Number(decisions[line.id]?.accepted ?? 0),
  }), { requested: 0, accepted: 0 });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { ...cardSx, p: 1 } }}>
      <DialogTitle sx={{ color: IPD_COLORS.textPrimary, fontWeight: 700 }}>
        Acknowledge Ward Return
        {header?.returnId ? ` — ${header.returnId}` : ''}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ color: IPD_COLORS.primary }} />
          </Box>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: IPD_COLORS.textSecondary, mb: 2 }}>
              Ward {header?.ward}
              {header?.patient_id && header.patient_id !== '—' ? ` · Patient ${header.patient_id}` : ''}
              {header?.admission_id && header.admission_id !== '—' ? ` · ${header.admission_id}` : ''}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <InvButton size="small" variant="outlined" tone="teal" onClick={onAcceptAll} disabled={!lines.length || submitting}>
                Accept all
              </InvButton>
              <InvButton size="small" variant="outlined" tone="red" onClick={onRejectAll} disabled={!lines.length || submitting}>
                Reject all lines
              </InvButton>
              {lines.length > 0 && (
                <Typography variant="caption" sx={{ color: IPD_COLORS.textSecondary }}>
                  Accepting {formatQty(totals.accepted)} of {formatQty(totals.requested)} units
                  across {lines.length} line{lines.length > 1 ? 's' : ''}
                </Typography>
              )}
            </Box>
            {!lines.length ? (
              <Typography color="text.secondary">No line items on this return.</Typography>
            ) : (
              <Box sx={{ overflowX: 'auto' }}>
                <Table
                  size="small"
                  sx={{ '& td, & th': { borderColor: IPD_COLORS.border, color: IPD_COLORS.textPrimary } }}
                >
                  <TableHead>
                    <TableRow>
                      {HEADERS.map((h) => <TableCell key={h} sx={headCell}>{h}</TableCell>)}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lines.map((line) => {
                      const accepted = Number(decisions[line.id]?.accepted ?? 0);
                      const rejected = Math.max(0, line.requested_quantity - accepted);
                      const needsReason = accepted < line.requested_quantity;
                      const itemLabel = resolveItemId?.(line.item_id) || line.item_id;
                      return (
                        <TableRow key={line.id}>
                          <TableCell>{itemLabel}</TableCell>
                          <TableCell>{line.batch_number}</TableCell>
                          <TableCell>{formatQty(line.requested_quantity)}</TableCell>
                          <TableCell sx={{ minWidth: 88 }}>
                            <InvTextField
                              type="number"
                              value={accepted}
                              onChange={(e) => onAcceptedChange(line.id, line.requested_quantity, e.target.value)}
                              inputProps={{
                                min: 0,
                                max: line.requested_quantity,
                                step: 'any',
                                'aria-label': `Accepted quantity for ${itemLabel}`,
                              }}
                              disabled={submitting}
                              sx={{ width: 88 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: IPD_COLORS.textSecondary }}>
                            {formatQty(rejected)}
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <InvTextField
                              fullWidth
                              placeholder={needsReason ? 'Required' : '—'}
                              value={decisions[line.id]?.reason || ''}
                              onChange={(e) => onReasonChange(line.id, e.target.value.slice(0, 500))}
                              disabled={submitting || !needsReason}
                              error={needsReason && !(decisions[line.id]?.reason || '').trim()}
                              inputProps={{ 'aria-label': 'Rejection reason' }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Box>
            )}
            <FormControlLabel
              sx={{ mt: 2, color: IPD_COLORS.textPrimary }}
              control={
                <Checkbox
                  checked={reverseBilling}
                  onChange={(e) => onReverseBillingChange(e.target.checked)}
                  disabled={submitting || !billable}
                  sx={{ color: IPD_COLORS.primary, '&.Mui-checked': { color: IPD_COLORS.primary } }}
                />
              }
              label="Also reverse the patient's billing for the accepted quantity"
            />
            {!billable && (
              <Typography variant="caption" sx={{ display: 'block', color: IPD_COLORS.textSecondary }}>
                No admission linked to this return — nothing to reverse in billing.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <InvButton variant="outlined" onClick={onClose} disabled={submitting}>
          Cancel
        </InvButton>
        <InvButton tone="teal" onClick={onSubmit} loading={submitting} disabled={loading || !lines.length}>
          Acknowledge
        </InvButton>
      </DialogActions>
    </Dialog>
  );
}
